<!--
Presentational componen for a single example of inflation. Used in SbInflationExampleSlide
-->
<template>
	<div
		class="col-span-5 grid grid-cols-subgrid grid-rows-subgrid items-center"
		v-if="blok"
		v-editable="blok"
	>
		<SbImage
			:blok="blok.image"
			class="h-28 object-contain"
		/>
		<div class="text-primary-600 font-serif text-3xl font-normal">
			{{ blok.name }}
		</div>
		<div class="text-secondary-900 font-sans text-base font-normal">
			{{ pastPriceFormatted }}
		</div>
		<div class="text-secondary-900 font-sans text-base font-normal">
			{{ presentPriceFormatted }}
		</div>
		<div class="flex items-center gap-2">
			<PlayIcon class="h-5 w-5 -rotate-90 text-red-500" />
			<span class="text-primary-700 font-sans text-4xl font-bold">{{
				increase
			}}</span>
		</div>
	</div>
</template>

<script setup lang="ts">
import SbImage from './SbImage.vue'
import { usePresentationLocale } from '@/i18n'
import type { InflationExampleStoryblok } from '@/sbTypes'
import { PlayIcon } from '@heroicons/vue/24/solid'
import { type PropType, computed } from 'vue'

const { n } = usePresentationLocale()

const props = defineProps({
	blok: Object as PropType<InflationExampleStoryblok>,
})

/** Number conversion of string pastPrice */
const pastPriceNum = computed(() => {
	if (!props.blok?.pastPrice) return undefined
	return parseFloat(props.blok?.pastPrice)
})

/** Number conversion of string presentPrice */
const presentPriceNum = computed(() => {
	if (!props.blok?.presentPrice) return undefined
	return parseFloat(props.blok?.presentPrice)
})

/** Human-readable version of pastPrice */
const pastPriceFormatted = computed(() => {
	return formatPrice(pastPriceNum.value)
})

/** Human-readable version of presentPrice */
const presentPriceFormatted = computed(() => {
	return formatPrice(presentPriceNum.value)
})

/** Percentage increase from pastPrice to presentPrice  */
const increase = computed(() => {
	if (!presentPriceNum.value || !pastPriceNum.value) return undefined

	const pct = presentPriceNum.value / pastPriceNum.value

	return n(pct, 'percent')
})

/** Format a price number as a string */
function formatPrice(price: number | null | undefined) {
	if (!price) return ''

	if (price > 1000)
		return n(price, {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0,
		})

	return n(price, 'currency')
}
</script>
